
import type { GridRow, GridColumn } from '../types/gridTypes';

export const computeSummary = (columns: GridColumn[], rows: GridRow[]) => {
  const summary: Record<string, { label: string; value: string | number }> = {};

  // Filter out aggregator rows to prevent double counting in sheet totals
  const leafRows = rows.filter(r => {
      const isAggregator = r.aggregationConfig?.enabled || 
                           ['group', 'subtotal', 'total'].includes(r.rowType || '');
      return !isAggregator;
  });

  columns.forEach(col => {
    if (!col.summaryMode || col.summaryMode === 'none') return;

    // Extract values. Handle numbers and dates (timestamps).
    const values = leafRows.map(r => {
        const v = r.values[col.id];
        // Attempt to parse if string looks numeric
        if (typeof v === 'string' && v.trim() !== '') {
            const f = parseFloat(v);
            return isNaN(f) ? null : f;
        }
        return typeof v === 'number' ? v : null;
    }).filter((v): v is number => v !== null);

    if (values.length === 0 && col.summaryMode !== 'count') {
      summary[col.id] = { label: col.summaryLabel || 'Total', value: '-' };
      return;
    }

    let result = 0;
    let display = '';

    switch (col.summaryMode) {
      case 'sum':
        result = values.reduce((a, b) => a + b, 0);
        break;
      case 'avg':
        result = values.reduce((a, b) => a + b, 0) / values.length;
        break;
      case 'min':
        result = Math.min(...values);
        break;
      case 'max':
        result = Math.max(...values);
        break;
      case 'count':
        // For count, we check the original row values for non-empty/non-null
        result = leafRows.filter(r => {
            const v = r.values[col.id];
            return v !== null && v !== undefined && v !== '';
        }).length;
        break;
    }

    // Formatting
    if (col.summaryMode === 'count') {
        display = String(result);
    } else {
        // Date handling for Min/Max
        if ((col.type === 'date' || col.semanticType === 'date') && (col.summaryMode === 'min' || col.summaryMode === 'max')) {
            display = new Date(result).toLocaleDateString();
        } 
        // Currency
        else if (col.type === 'currency') {
            display = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(result);
        } 
        // Percent
        else if (col.type === 'percent') {
            display = `${(result * 100).toFixed(1)}%`;
        } 
        // Default Number
        else {
            // Use fewer decimals for integers
            display = Number.isInteger(result) ? String(result) : result.toFixed(2);
        }
    }

    const modeLabels: Record<string, string> = {
        sum: 'Sum',
        avg: 'Avg',
        min: 'Min',
        max: 'Max',
        count: 'Count'
    };

    summary[col.id] = {
      label: col.summaryLabel || modeLabels[col.summaryMode] || 'Total',
      value: display
    };
  });

  return summary;
};
